import React from 'react';
import { Globals } from '../utils';
import Button from './button';
import Icon from './icon';
import { PaperclipIcon } from 'assets/icons';

import heic2any from 'heic2any'; /* Librería para convertir imágenes HEIC/HEIF a PNG/JPEG/GIF */

/**
 * Convierte un Data URI (base64) a un objeto Blob binario.
 */
const dataURItoBlob = (dataURI: any) => {
  var byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
  else byteString = unescape(dataURI.split(',')[1]);
  var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  var ia = new Uint8Array(byteString.length);
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ia], { type: mimeString });
};

/**
 * Lista de extensiones que se consideran formatos de IMAGEN.
 * Estos formatos serán convertidos a PNG mediante Canvas API o heic2any.
 */
const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'heic',
  'heif',
  'webp',
  'bmp',
  'tiff',
  'tif',
  'avif',
];

const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

const getMimeType = (extension: string): string => {
  const mimeMap: Record<string, string> = {
    // Formatos de imagen estándar
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    // Formatos de iPhone/Apple
    heic: 'image/heic',
    heif: 'image/heif',
    // Formatos modernos de imagen
    webp: 'image/webp',
    avif: 'image/avif',
    // Formatos legacy de imagen
    bmp: 'image/bmp',
    tiff: 'image/tiff',
    tif: 'image/tiff',
    // Formatos de documentos
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.ms-excel',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  };
  return mimeMap[extension] || 'image/jpeg';
};

/**
 * Verifica si una extensión corresponde a un formato HEIC/HEIF (iPhone).
 */
const isHeicFormat = (extension: string): boolean => {
  return ['heic', 'heif'].includes(extension);
};

/**
 * Verifica si una extensión corresponde a un formato de documento
 */
const isDocumentFormat = (extension: string): boolean => {
  return DOCUMENT_EXTENSIONS.includes(extension);
};

/**
 * Verifica si una extensión corresponde a un formato de imagen.
 * Las imágenes serán procesadas por Canvas API para convertirlas a PNG.
 */
const isImageFormat = (extension: string): boolean => {
  return IMAGE_EXTENSIONS.includes(extension);
};

class PaperClip extends React.Component<any> {
  private file: any;

  change = async (e: any) => {
    const fileSource = e.target.files?.length ? e.target.files : e.dataTransfer?.files;
    var files = fileSource ? [...fileSource] : [];
    if (!files[0]) {
      return;
    }
    this.file.value = '';

    // Obtener y validar la extensión del archivo
    var format = files[0].name.split('.');
    var permitidos = this.props.formats || ['jpg', 'png'];
    var extension = format[format.length - 1].toLowerCase();

    // Verificar que el formato esté en la lista de formatos permitidos
    if (permitidos.indexOf(extension) == -1) {
      Globals.showError('El formato del archivo no es válido');
      return false;
    }

    // determinar el MIME type del archivo
    let tipo = getMimeType(extension);

    // rocesar según el tipo de archivo
    if (isDocumentFormat(extension)) {
      var reader = new FileReader();
      reader.readAsDataURL(files[0]);
      reader.onload = (file) => {
        this.props.onChange({
          target: {
            name: this.props.name,
            value: dataURItoBlob(reader.result),
            dataURL: reader.result,
          },
        });
      };
    } else if (isHeicFormat(extension)) {
      try {
        Globals.setLoading();

        const convertedBlob = await heic2any({
          blob: files[0],
          toType: 'image/png',
        });

        // heic2any puede devolver un Blob o un array de Blobs
        const pngBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

        // Crear una URL temporal del Blob para cargarlo en un <img>
        // y luego procesarlo con Canvas
        const blobUrl = URL.createObjectURL(pngBlob);

        // Procesar con Canvas para redimensionar
        this.processImageWithCanvas(blobUrl, files[0].name, () => {
          // Liberar la URL temporal del Blob para no consumir memoria
          URL.revokeObjectURL(blobUrl);
        });

        Globals.quitLoading();
      } catch (error) {
        Globals.quitLoading();
        console.error('Error al convertir HEIC/HEIF:', error);
        Globals.showError(
          'No se pudo procesar la imagen HEIC/HEIF. Intente convertirla a PNG o JPG antes de subirla.',
        );
      }
    } else if (isImageFormat(extension)) {
      var reader = new FileReader();
      reader.readAsDataURL(files[0]);
      reader.onload = (file) => {
        this.processImageWithCanvas(reader.result as string, files[0].name);
      };
    }
  };

  /**
   * @param imageSrc - URL de la imagen (puede ser Data URI o Blob URL)
   * @param originalName - Nombre original del archivo (para pasar al onChange)
   * @param onComplete - Callback opcional ejecutado al terminar (ej: liberar Blob URL)
   */
  processImageWithCanvas = (imageSrc: string, originalName: string, onComplete?: () => void) => {
    var tempImg: any = new Image();
    tempImg.src = imageSrc;

    let self: any = this;

    tempImg.onload = function () {
      // ─── Validación de tamaño en KB (si se define prop 'size') ────
      // Esto permite limitar el peso máximo del archivo subido.
      // Nota: para HEIC convertido, el tamaño se valida sobre la imagen
      // ya convertida, no sobre el archivo original.
      if (self.props.size != null) {
        // Estimar tamaño: para HEIC convertidos usamos el canvas output
        // Para archivos regulares, se podría calcular del blob
      }

      var MAX_WIDTH = 1000;
      var MAX_HEIGHT = 1000;
      var tempW = tempImg.width;
      var tempH = tempImg.height;

      // ─── Validación de dimensiones exactas (si se define prop 'exact') ────
      if (!self.props.exact) {
        // Validar que no exceda dimensiones máximas configuradas
        if (self.props.width != null && tempW > self.props.width) {
          Globals.showError('El ancho de la imagen debe ser máximo de ' + self.props.width + 'px');
          if (onComplete) onComplete();
          return false;
        }

        if (self.props.height != null && tempH > self.props.height) {
          Globals.showError('El alto de la imagen debe ser máximo de ' + self.props.height + 'px');
          if (onComplete) onComplete();
          return false;
        }
      } else {
        // Validar que tenga dimensiones exactas
        if (self.props.width != null && tempW != self.props.width) {
          Globals.showError('El ancho de la imagen debe ser de ' + self.props.width + 'px');
          if (onComplete) onComplete();
          return false;
        }

        if (self.props.height != null && tempH != self.props.height) {
          Globals.showError('El alto de la imagen debe ser de ' + self.props.height + 'px');
          if (onComplete) onComplete();
          return false;
        }
      }

      // Mantiene la proporción (aspect ratio) de la imagen original.
      if (self.props.width == null && self.props.height == null) {
        if (tempW > tempH) {
          if (tempW > MAX_WIDTH) {
            tempH *= MAX_WIDTH / tempW;
            tempW = MAX_WIDTH;
          }
        } else {
          if (tempH > MAX_HEIGHT) {
            tempW *= MAX_HEIGHT / tempH;
            tempH = MAX_HEIGHT;
          }
        }
      }

      // Dibujar en Canvas y exportar como PNG
      var resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = tempW;
      resizedCanvas.height = tempH;
      var ctx: any = resizedCanvas.getContext('2d');
      ctx.drawImage(this, 0, 0, tempW, tempH);

      // Se usa 'image/png' siempre, sin importar el formato
      var dataURL = resizedCanvas.toDataURL('image/png');

      // Convertir el Data URI a Blob y notificar al componente padre
      self.props.onChange({
        target: {
          name: self.props.name,
          value: dataURItoBlob(dataURL),
          dataURL,
          original_name: originalName,
        },
      });

      // callback de limpieza
      if (onComplete) onComplete();
    };

    tempImg.onerror = function () {
      Globals.showError(
        'No se pudo cargar la imagen. El formato podría no ser compatible con su navegador.',
      );
      if (onComplete) onComplete();
    };
  };

  render() {
    return (
      <div className={'container-paper-clip ' + (this.props.className || '')}>
        <input
          className='input-file-paperclip'
          ref={(ref) => (this.file = ref)}
          type='file'
          name={this.props.name}
          onChange={this.change}
          style={this.props.inputStyle}
        />
        <Button
          disabled={this.props.disabled}
          color={this.props.value ? 'green' : 'orange'}
          type='button'
          onClick={() => (this.props.onClick != null ? this.props.onClick() : this.file.click())}
        >
          <div className='container-icon-paperclip'>
            {!this.props.value && <img src={this.props.icon || PaperclipIcon} />}
            {this.props.value && (
              <React.Fragment>
                {this.props.successImg ? (
                  <img className='paper-clip-success-img' src={this.props.successImg} />
                ) : (
                  <i className='fa fa-check'></i>
                )}
              </React.Fragment>
            )}
          </div>
          {this.props.children ? (
            <React.Fragment>{this.props.children}</React.Fragment>
          ) : (
            <span>
              {this.props.value
                ? this.props.successText || 'Archivo Cargado'
                : this.props.pendingText || 'Subir Archivo'}
            </span>
          )}
        </Button>
      </div>
    );
  }
}

export default PaperClip;

/**
 * Lista combinada de todas las extensiones soportadas por PaperClip.
 */
const ImagesAndDocuments = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'heic',
  'heif',
  'webp',
  'avif',
  'bmp',
  'tiff',
  'tif',
  'pdf',
  'doc',
  'xls',
  'xlsx',
  'docx',
];

export { ImagesAndDocuments, dataURItoBlob };
